/** In-memory dashboard GET cache + in-flight dedupe (clinic/period scoped). */

export type DashboardPeriodCacheParams = {
  clinicId: string
  date: string
  period: string
  [key: string]: unknown
}

type CacheEntry = {
  data: unknown
  expiresAt: number
}

type InflightEntry = {
  promise: Promise<unknown>
  subscribers: number
}

const DEFAULT_STALE_MS = 180_000

const resultCache = new Map<string, CacheEntry>()
const inflightCache = new Map<string, InflightEntry>()

/** Stable ids for fetcher functions. Production minification can give many
 *  overview/detail helpers the same `.name` (e.g. "s"), so we must not key
 *  the period cache off Function.name alone. */
const fetcherIdentityKeys = new WeakMap<object, string>()
let nextFetcherIdentityId = 0

/**
 * Cache-key fragment for a dashboard period fetcher.
 * Prefer an explicit `cacheKey` when provided; otherwise identity the function
 * object so two differently named APIs cannot collide after minify.
 */
export function resolveDashboardPeriodFetcherKey(
  fetcher: object,
  explicitCacheKey?: string,
): string {
  if (explicitCacheKey) return explicitCacheKey
  const existing = fetcherIdentityKeys.get(fetcher)
  if (existing) return existing
  const key = `dashboard-fetch-${nextFetcherIdentityId++}`
  fetcherIdentityKeys.set(fetcher, key)
  return key
}

function stableExtraParams(params: DashboardPeriodCacheParams): string {
  const extras = Object.keys(params)
    .filter((key) => key !== 'clinicId' && key !== 'date' && key !== 'period')
    .sort()
    .map((key) => `${key}=${String(params[key])}`)
  return extras.join('&')
}

export function buildDashboardPeriodCacheKey(
  fetcherName: string,
  params: DashboardPeriodCacheParams,
): string {
  const extra = stableExtraParams(params)
  const base = `${fetcherName}|${params.clinicId}|${params.date}|${params.period}`
  return extra ? `${base}|${extra}` : base
}

export function readDashboardPeriodCache<T>(key: string): T | undefined {
  const entry = resultCache.get(key)
  if (!entry) return undefined
  if (Date.now() > entry.expiresAt) {
    resultCache.delete(key)
    return undefined
  }
  return entry.data as T
}

export function writeDashboardPeriodCache(
  key: string,
  data: unknown,
  staleMs: number = DEFAULT_STALE_MS,
): void {
  resultCache.set(key, { data, expiresAt: Date.now() + staleMs })
}

export function getDashboardPeriodInflight<T>(
  key: string,
): Promise<T> | undefined {
  return inflightCache.get(key)?.promise as Promise<T> | undefined
}

/** Register ownership of a new in-flight request (subscriber count starts at 1). */
export function setDashboardPeriodInflight<T>(
  key: string,
  promise: Promise<T>,
): Promise<T> {
  inflightCache.set(key, { promise, subscribers: 1 })
  // `finally` returns a new promise that re-rejects; catch so abort/cancel
  // never surfaces as an unhandled rejection from this bookkeeping alone.
  void promise
    .finally(() => {
      const entry = inflightCache.get(key)
      if (entry?.promise === promise) {
        inflightCache.delete(key)
      }
    })
    .catch(() => undefined)
  return promise
}

/** Join an existing in-flight request. Returns false if nothing to join. */
export function joinDashboardPeriodInflight(key: string): boolean {
  const entry = inflightCache.get(key)
  if (!entry) return false
  entry.subscribers += 1
  return true
}

/**
 * Leave an in-flight request. Returns true when this was the last subscriber
 * (caller may abort an owned request).
 */
export function leaveDashboardPeriodInflight(
  key: string,
  promise?: Promise<unknown>,
): boolean {
  const entry = inflightCache.get(key)
  if (!entry) return true
  if (promise != null && entry.promise !== promise) return false
  entry.subscribers -= 1
  if (entry.subscribers <= 0) {
    inflightCache.delete(key)
    return true
  }
  return false
}

/** Drop a specific in-flight promise (e.g. after abort) so remounts do not reuse it. */
export function clearDashboardPeriodInflight(
  key: string,
  promise?: Promise<unknown>,
): void {
  if (promise == null) {
    inflightCache.delete(key)
    return
  }
  const entry = inflightCache.get(key)
  if (entry?.promise === promise) {
    inflightCache.delete(key)
  }
}

export function invalidateDashboardPeriodCache(keyPrefix?: string): void {
  if (!keyPrefix) {
    resultCache.clear()
    return
  }
  for (const key of Array.from(resultCache.keys())) {
    if (key.startsWith(keyPrefix)) resultCache.delete(key)
  }
}

export const DASHBOARD_PERIOD_CACHE_STALE_MS = DEFAULT_STALE_MS
